
import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { baseUrl, config, requestID, getUserData } from "../common/utils";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Button,
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  TablePagination,
  Stack,
  Avatar,
  CircularProgress,
  TextField,
  Snackbar,
  Alert,
  Autocomplete
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  AttachMoney as AttachMoneyIcon,
  Store as StoreIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  ReportProblem as ReportIcon,
  Send as SendIcon,
  HourglassEmpty as PendingIcon,
  SwapHoriz as SwapIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';

// --- Interfaces ---
export interface StockItem {
  stock_name: string;
  ecid_item: string;
  item_id: string;
  quantity: number | string;
  unit_price: number | string;
  department_name: string;
  department_id?: string;
  reorder_level?: number | string; // Added field
}

interface HistoryItem {
  created_at: string;
  movement_type: string;
  quantity: string;
  from_dept: string | null;
  to_dept: string | null;
  reviewer_name: string;
}

interface PendingRequest {
  id: string | number;
  department_id: string;
  quantity_to_deduct: string;
  reason: string;
  created_at: string;
}

interface Department {
  id: string | number;
  name: string;
  department_id: string; 
}

interface DashboardProps {
  stocks: StockItem[];
  loading: boolean;
  onRefresh: () => void;
onTransfer?: (item: StockItem) => void;
}
const StockDashboard: React.FC<DashboardProps> = ({ stocks, loading, onRefresh, onTransfer }) => {
  const [selectedItem, setSelectedItem] = useState<StockItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  
  const [isLevelDialogOpen, setIsLevelDialogOpen] = useState(false);
  const [tempLevel, setTempLevel] = useState('');

  const [isReportOpen, setIsReportOpen] = useState(false);
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [deptList, setDeptList] = useState<Department[]>([]);

  const [writeOffQty, setWriteOffQty] = useState('');
  const [writeOffReason, setWriteOffReason] = useState('');
  
  const [transferQty, setTransferQty] = useState('');
  const [transferToDept, setTransferToDept] = useState<Department | null>(null); 
  const [transferReason, setTransferReason] = useState(''); 

  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' as 'success'|'error' });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  // Updated Metrics to use Dynamic Reorder Level
  const metrics = useMemo(() => {
    const totalItems = stocks.length;
    const lowStockCount = stocks.filter(s => {
        const threshold = Number(s.reorder_level) || 5;
        return Number(s.quantity) <= threshold;
    }).length;
    const totalStockQty = stocks.reduce((acc, curr) => acc + Number(curr.quantity), 0);
    const distinctDepts = new Set(stocks.map(s => s.department_name)).size;
    return { totalItems, lowStockCount, totalStockQty, distinctDepts };
  }, [stocks]);

  const handleChangePage = (event: unknown, newPage: number) => { setPage(newPage); };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleStocks = useMemo(() => {
    return stocks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [stocks, page, rowsPerPage]);

  // --- ACTIONS ---
  
  const openModal = async (item: StockItem) => {
    setSelectedItem(item);
    setIsModalOpen(true);
    setHistory([]); 
    setPendingRequests([]);
    setHistoryLoading(true);

    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("item_id", item.item_id);

      const response = await axios.post(`${baseUrl()}Geacloud_Inventorys/getItemHistory`, formData, config);
      if (response.data.status === true) {
        setHistory(response.data.data.history || []);
        setPendingRequests(response.data.data.pending || []);
      }
    } catch (error) {
      console.error("History fetch error", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedItem(null), 200);
  };

  const handleUpdateLevel = async () => {
    if (!selectedItem) return;
    setSubmitting(true);
    try {
        const formData = new FormData();
        formData.append("item_id", selectedItem.item_id);
        formData.append("level", tempLevel);

        const response = await axios.post(`${baseUrl()}Geacloud_Inventorys/setMinStock`, formData, config);
        console.log("Min-Stock:", response.data);
        if (response.data.status) {
            setToast({ open: true, msg: "Reorder level updated!", severity: 'success' });
            setIsLevelDialogOpen(false);
            onRefresh(); 
        }
    } catch (error) {
        setToast({ open: true, msg: "Error updating level", severity: 'error' });
    } finally {
        setSubmitting(false);
    }
  };

  const handleSubmitWriteOff = async () => {
    if (!selectedItem || !writeOffQty || !writeOffReason) {
        setToast({ open: true, msg: "Please fill in quantity and reason.", severity: 'error' });
        return;
    }
    setSubmitting(true);
    const currentUserId = getUserData()?.id || 0;

    try {
        const formData = new FormData();
        formData.append("requestID", requestID());
        formData.append("user_id", String(currentUserId));
        formData.append("item_id", selectedItem.item_id);
        formData.append("department_id", selectedItem.department_id || "0");
        formData.append("from_department_id", selectedItem.department_id || "0"); 
        formData.append("to_department_id", transferToDept.department_id);
        formData.append("quantity", writeOffQty);
        formData.append("reason", writeOffReason);

        const response = await axios.post(`${baseUrl()}Geacloud_Inventorys/requestAdjustment`, formData, config);
        if (response.data.status === true || response.data.status === "true") {
            setToast({ open: true, msg: "Request submitted for approval!", severity: 'success' });
            setIsReportOpen(false);
            openModal(selectedItem); 
        } else {
            setToast({ open: true, msg: response.data.message || "Request failed", severity: 'error' });
        }
    } catch (error) {
        setToast({ open: true, msg: "Network error submitting request", severity: 'error' });
    } finally {
        setSubmitting(false);
    }
  };

  const handleSubmitTransfer = async () => {
    if (!selectedItem || !transferQty || !transferToDept) {
        setToast({ open: true, msg: "Please fill in quantity and destination.", severity: 'error' });
        return;
    }
    setSubmitting(true);
    try {
        const formData = new FormData();
        formData.append("requestID", requestID());
        formData.append("item_id", selectedItem.item_id);
        formData.append("from_department_id", selectedItem.department_id || "0"); 
        formData.append("to_department_id", transferToDept.department_id);
        formData.append("quantity", transferQty);
        formData.append("reviewer_name", "Direct Transfer"); 
        formData.append("signer_out_name", "Direct Transfer");

        const response = await axios.post(`${baseUrl()}Geacloud_Inventorys/transferStock`, formData, config);
        if (response.data.status === true) {
            setToast({ open: true, msg: "Stock transferred successfully!", severity: 'success' });
            setIsTransferOpen(false);
            onRefresh(); 
            closeModal(); 
        }
    } catch (error) {
        setToast({ open: true, msg: "Network error", severity: 'error' });
    } finally {
        setSubmitting(false);
    }
  };

  // Add this inside the StockDashboard component to fill the dropdown
useEffect(() => {
  const fetchDepts = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      const res = await axios.post(`${baseUrl()}Geacloud_Inventorys/getDepartments`, formData, config);
      if (res.data.status) {
        setDeptList(res.data.data || []); // Fill the deptList state
      }
    } catch (err) {
      console.error("Failed to fetch departments for transfer:", err);
    }
  };

  fetchDepts();
}, []);

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}> 
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2.5, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: '#e0f2fe', color: '#0284c7', width: 48, height: 48 }}> <InventoryIcon /> </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>TOTAL ITEMS</Typography>
                <Typography variant="h5" fontWeight={700} color="text.primary">{metrics.totalItems}</Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2.5, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
             <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: '#dcfce7', color: '#16a34a', width: 48, height: 48 }}> <StoreIcon /> </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>TOTAL QTY</Typography>
                <Typography variant="h5" fontWeight={700} color="#16a34a">{metrics.totalStockQty.toLocaleString()}</Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2.5, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
             <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: '#fee2e2', color: '#dc2626', width: 48, height: 48 }}> <WarningIcon /> </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>LOW STOCK</Typography>
                <Typography variant="h5" fontWeight={700} color="#dc2626">{metrics.lowStockCount}</Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 2.5, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
             <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: '#f3f4f6', color: '#4b5563', width: 48, height: 48 }}> <AttachMoneyIcon /> </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>DEPARTMENTS</Typography>
                <Typography variant="h5" fontWeight={700} color="text.primary">{metrics.distinctDepts}</Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      {/* Main Table */}
      <Card sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
          <Box>
            <Typography variant="h6" fontWeight={700} color="#1e293b">Transaction History</Typography>
            <Typography variant="body2" color="text.secondary">Showing {visibleStocks.length} of {stocks.length} records</Typography>
          </Box>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onRefresh} disabled={loading} sx={{ textTransform: 'none', borderRadius: 2 }}>
            {loading ? 'Refreshing...' : 'Refresh Data'}
          </Button>
        </Box>

        <TableContainer sx={{ maxHeight: 600 }}> 
          <Table stickyHeader aria-label="stock table" sx={{ minWidth: 650 }}> 
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>ITEM NAME</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>SKU / REF</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>DEPARTMENT</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: '#64748b' }}>PRICE</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#64748b' }}>STATUS</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: '#64748b' }}>QTY</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#64748b' }}>ACTION</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stocks.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}><Typography color="text.secondary">No items found.</Typography></TableCell></TableRow>
              ) : (
                visibleStocks.map((item, index) => {
                  const threshold = Number(item.reorder_level) || 5; 
                  const isLowStock = Number(item.quantity) <= threshold;
                  return (
                    <TableRow key={index} hover>
                      <TableCell sx={{ fontWeight: 500, color: '#0f172a' }}>{item.stock_name}</TableCell>
                      <TableCell sx={{ color: '#64748b', fontFamily: 'monospace' }}>{item.ecid_item}</TableCell>
                      <TableCell><Chip label={item.department_name} size="small" sx={{ bgcolor: '#f1f5f9' }} /></TableCell>
                      <TableCell align="right">₦{Number(item.unit_price).toFixed(2)}</TableCell>
                      <TableCell align="center">
                        <Chip label={isLowStock ? "Low Stock" : "Active"} color={isLowStock ? "error" : "success"} size="small" />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: isLowStock ? '#dc2626' : '#16a34a' }}>{item.quantity}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <IconButton onClick={() => openModal(item)} size="small" sx={{ color: '#94a3b8' }}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            onClick={() => {
                              setSelectedItem(item);
                              setTempLevel(String(item.reorder_level || 5)); 
                              setIsLevelDialogOpen(true);
                            }} 
                            size="small" 
                            sx={{ color: '#94a3b8', '&:hover': { color: '#4f46e5' } }}
                          >
                            <SettingsIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination rowsPerPageOptions={[25, 50, 100]} component="div" count={stocks.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
      </Card>

      {/* History Modal */}
      <Dialog open={isModalOpen} onClose={closeModal} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        {selectedItem && (
          <>
            <Box sx={{ background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)', p: 3, color: 'white', position: 'relative' }}>
              <IconButton onClick={closeModal} sx={{ position: 'absolute', top: 12, right: 12, color: 'white' }}> <CloseIcon /> </IconButton>
              <Typography variant="h5" fontWeight={700}>{selectedItem.stock_name}</Typography>
              <Typography variant="body2">Ref: {selectedItem.ecid_item} | Loc: {selectedItem.department_name}</Typography>
            </Box>
            <DialogContent sx={{ p: 0 }}>
              {pendingRequests.length > 0 && (
                <Box sx={{ p: 2, bgcolor: '#fff7ed' }}>
                    <Alert severity="warning">
                        <Typography variant="subtitle2" fontWeight={700}>Pending Approval</Typography>
                        {pendingRequests.map(req => (
                            <Typography key={req.id} variant="body2">Remove {req.quantity_to_deduct} units ({req.reason})</Typography>
                        ))}
                    </Alert>
                </Box>
              )}
              <Box sx={{ p: 4 }}>
                <Stack direction="row" spacing={2}>
<Button 
  variant="contained" 
  startIcon={<SwapIcon />} 
  onClick={() => {
    if (onTransfer && selectedItem) onTransfer(selectedItem); 
        setIsTransferOpen(true); 
  }}
>
  Transfer
</Button>                  <Button variant="outlined" color="error" startIcon={<ReportIcon />} onClick={() => setIsReportOpen(true)}>Report Damage</Button>
                </Stack>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* REORDER LEVEL SETTINGS DIALOG */}
      <Dialog open={isLevelDialogOpen} onClose={() => setIsLevelDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Set Reorder Level</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Adjust the minimum quantity for <strong>{selectedItem?.stock_name}</strong>. You will be alerted when stock falls below this number.
            </Typography>
            <TextField 
                label="Reorder Threshold" 
                type="number" 
                fullWidth 
                value={tempLevel} 
                onChange={(e) => setTempLevel(e.target.value)} 
                autoFocus
            />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setIsLevelDialogOpen(false)} color="inherit">Cancel</Button>
            <Button onClick={handleUpdateLevel} variant="contained" color="primary" disabled={submitting}>
                {submitting ? <CircularProgress size={24} /> : 'Save Level'}
            </Button>
        </DialogActions>
      </Dialog>

      {/* REPORT DAMAGE DIALOG */}
      <Dialog open={isReportOpen} onClose={() => setIsReportOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: '#fff1f2', color: '#be123c', fontWeight: 700 }}>Report Damage</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
            <TextField label="Quantity" type="number" fullWidth sx={{ mb: 2 }} value={writeOffQty} onChange={(e) => setWriteOffQty(e.target.value)} />
            <TextField label="Reason" fullWidth multiline rows={2} value={writeOffReason} onChange={(e) => setWriteOffReason(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setIsReportOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmitWriteOff} variant="contained" color="error" disabled={submitting}>Submit</Button>
        </DialogActions>
      </Dialog>

        {/* TRANSFER STOCK DIALOG */}
<Dialog open={isTransferOpen} onClose={() => setIsTransferOpen(false)} maxWidth="xs" fullWidth>
  <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid #e2e8f0' }}>
    Internal Stock Transfer
  </DialogTitle>
  <DialogContent sx={{ pt: 3 }}>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
      Moving <strong>{selectedItem?.stock_name}</strong> from <strong>{selectedItem?.department_name}</strong> to another unit.
    </Typography>
    
    <Stack spacing={3}>
      <TextField 
        label="Quantity to Transfer" 
        type="number" 
        fullWidth 
        value={transferQty} 
        onChange={(e) => setTransferQty(e.target.value)} 
      />

      <Autocomplete
        options={deptList} // Use the departments list we fetched earlier
        getOptionLabel={(option) => option.name}
        onChange={(e, newValue) => setTransferToDept(newValue)}
        renderInput={(params) => <TextField {...params} label="Destination Unit / Ward" />}
      />

      <TextField 
        label="Transfer Remarks" 
        fullWidth 
        multiline 
        rows={2} 
        value={transferReason} 
        onChange={(e) => setTransferReason(e.target.value)} 
      />
    </Stack>
  </DialogContent>
  <DialogActions sx={{ p: 2 }}>
    <Button onClick={() => setIsTransferOpen(false)} color="inherit">Cancel</Button>
    <Button 
      onClick={handleSubmitTransfer} 
      variant="contained" 
      disabled={submitting || !transferQty || !transferToDept}
    >
      {submitting ? <CircularProgress size={24} /> : 'Complete Transfer'}
    </Button>
  </DialogActions>
</Dialog>


      {/* Toast notifications */}
      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast({...toast, open: false})} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default StockDashboard;